// Blog hero illustrations: flat geometric motifs in public/images/illustrations/,
// resolved per post from the internal-link mesh clusters (data/internal-links.ts).
// A post may override with frontmatter `illustration: '<motif>'`.

import { LINK_MESH } from './internal-links';

export interface BlogIllustration {
  file: string;
  alt: string;
}

export const blogIllustrations: Record<string, BlogIllustration> = {
  clients: { file: 'clients.svg', alt: 'Illustration: client records and relationships' },
  inbox: { file: 'inbox.svg', alt: 'Illustration: shared inbox and conversations' },
  voice: { file: 'voice.svg', alt: 'Illustration: calls and voicemail' },
  schedule: { file: 'schedule.svg', alt: 'Illustration: scheduling and calendar' },
  proposals: { file: 'proposals.svg', alt: 'Illustration: proposals and quotes' },
  invoices: { file: 'invoices.svg', alt: 'Illustration: invoices and billing' },
  payments: { file: 'payments.svg', alt: 'Illustration: payments and expenses' },
  automation: { file: 'automation.svg', alt: 'Illustration: workflow automations' },
  reporting: { file: 'reporting.svg', alt: 'Illustration: reporting and analytics' },
  portal: { file: 'portal.svg', alt: 'Illustration: client portal and onboarding' },
  general: { file: 'general.svg', alt: 'Illustration: the Workspace369 workspace' },
  /* Per-vertical motifs */
  'interior-design': { file: 'interior-design.svg', alt: 'Illustration: floor plan with furniture and measurements' },
  'field-service': { file: 'field-service.svg', alt: 'Illustration: service van with wrench badge and route pin' },
  bookkeeping: { file: 'bookkeeping.svg', alt: 'Illustration: ledger book with calculator and balanced columns' },
  msp: { file: 'msp.svg', alt: 'Illustration: monitor with server rack and status checks' },
  photography: { file: 'photography.svg', alt: 'Illustration: camera with aperture blades and framed photo' },
  'event-planning': { file: 'event-planning.svg', alt: 'Illustration: calendar page with confetti and checklist' },
  coaching: { file: 'coaching.svg', alt: 'Illustration: target with arrow and upward growth line' },
  remodeling: { file: 'remodeling.svg', alt: 'Illustration: house with hammer and saw' },
  freelance: { file: 'freelance.svg', alt: 'Illustration: laptop with coffee and a clock' },
  architecture: { file: 'architecture.svg', alt: 'Illustration: blueprint with drafting compass and ruler' },
  pr: { file: 'pr.svg', alt: 'Illustration: megaphone with broadcast arcs and press release' },
  fitness: { file: 'fitness.svg', alt: 'Illustration: dumbbell with a progress chart' },
  consulting: { file: 'consulting.svg', alt: 'Illustration: briefcase with an ascending line chart' },
  tutoring: { file: 'tutoring.svg', alt: 'Illustration: open book with a graduation cap' },
  salon: { file: 'salon.svg', alt: 'Illustration: open scissors before a round mirror with a sparkle' },
  video: { file: 'video.svg', alt: 'Illustration: clapperboard with a play button' },
  travel: { file: 'travel.svg', alt: 'Illustration: suitcase with a flight arc and a map pin' },
  moving: { file: 'moving.svg', alt: 'Illustration: box truck beside a stack of moving boxes' },
  inspection: { file: 'inspection.svg', alt: 'Illustration: house outline with a magnifying glass and check' },
  driving: { file: 'driving.svg', alt: 'Illustration: steering wheel over a road ribbon' },
  security: { file: 'security.svg', alt: 'Illustration: shield with checkmark and radio signal' },
  junk: { file: 'junk.svg', alt: 'Illustration: truck loaded with boxes and recycle arc' },
  cleaning: { file: 'cleaning.svg', alt: 'Illustration: spray bottle with sparkles and shine arc' },
  grooming: { file: 'grooming.svg', alt: 'Illustration: pet grooming with paw print and scissors' },
  detailing: { file: 'detailing.svg', alt: 'Illustration: car side profile with foam bubbles and sparkle' },
  'free-tools': { file: 'free-tools.svg', alt: 'Illustration: calculator with percent badge and wrench' },
};

// mesh cluster id → motif. Every vertical cluster has its own motif.
// (No pressure-washing/plumbing motifs exist — do not add mappings for them.)
export const CLUSTER_ILLUSTRATION: Record<string, string> = {
  /* topics */
  'invoicing-billing': 'invoices',
  proposals: 'proposals',
  scheduling: 'schedule',
  'client-portal': 'portal',
  voice: 'voice',
  communications: 'inbox',
  expenses: 'payments',
  profitability: 'reporting',
  'crm-clients': 'clients',
  'agency-ops': 'reporting',
  'google-workspace': 'inbox',
  'microsoft-365': 'inbox',
  'free-tools': 'free-tools',
  /* verticals */
  'vertical-design': 'interior-design',
  'vertical-field': 'field-service',
  'vertical-books': 'bookkeeping',
  'vertical-msp': 'msp',
  'vertical-photo': 'photography',
  'vertical-event': 'event-planning',
  'vertical-coaching': 'coaching',
  'vertical-remodeling': 'remodeling',
  'vertical-freelance': 'freelance',
  'vertical-arch': 'architecture',
  'vertical-pr': 'pr',
  'vertical-fitness': 'fitness',
  'vertical-consulting': 'consulting',
  'vertical-tutoring': 'tutoring',
  'vertical-salon': 'salon',
  'vertical-video': 'video',
  'vertical-travel': 'travel',
  'vertical-moving': 'moving',
  'vertical-inspection': 'inspection',
  'vertical-driving': 'driving',
  'vertical-security': 'security',
  'vertical-junk': 'junk',
  'vertical-cleaning': 'cleaning',
  'vertical-grooming': 'grooming',
  'vertical-detailing': 'detailing',
};

// Resolution order: a post's own vertical identity beats topic clusters
// (vertical posts get their own art); then specific topics beat generic ones;
// 'general' is the fallback for unmapped clusters and mesh-less posts.
const TOPIC_PRIORITY = [
  'proposals',
  'invoicing-billing',
  'client-portal',
  'voice',
  'communications',
  'expenses',
  'scheduling',
  'profitability',
  'crm-clients',
  'agency-ops',
  'google-workspace',
  'microsoft-365',
  'free-tools',
];

export function blogIllustrationForClusters(clusters: readonly string[] | undefined): string {
  const set = new Set(clusters || []);
  for (const cluster of set) {
    if (cluster.startsWith('vertical-') && CLUSTER_ILLUSTRATION[cluster]) {
      return CLUSTER_ILLUSTRATION[cluster];
    }
  }
  for (const id of TOPIC_PRIORITY) {
    if (set.has(id)) return CLUSTER_ILLUSTRATION[id];
  }
  // safety net for future vertical clusters without their own motif yet
  if ([...set].some((cluster) => cluster.startsWith('vertical-'))) return 'schedule';
  return 'general';
}

// Full per-post resolution: frontmatter override (a known motif) → mesh clusters
// → 'general' (also when the post has no mesh entry).
export function blogIllustrationMotifForPath(path: string, override?: unknown): string {
  if (typeof override === 'string' && blogIllustrations[override]) return override;
  return blogIllustrationForClusters(LINK_MESH[path]?.clusters);
}

export function blogIllustrationForMotif(motif: string) {
  const illustration = blogIllustrations[motif];
  if (!illustration) return null;
  return { src: `/images/illustrations/${illustration.file}`, alt: illustration.alt };
}
